import { call, put, select, take, takeEvery } from 'redux-saga/effects';

import dynaForms, { getActions, getSelectors, getTypes } from '@elab/core/utils/dynaForms';
import dynaFormsApi from '@elab/core/utils/dynaForms/api';
import {
    emailChangeFormErrorMessage,
    emailChangeFormSuccessMessage,
} from '@elab/core/utils/constants';
import { actions as authActions, types as authTypes } from '@elab/core/store/auth';
import { actions as globalActions } from '@elab/core/store/global';
import { actions as uiActions } from '@elab/core/store/ui';
import { selectors as configSelectors } from '@elab/core/store/config';
import { selectors as userSelectors } from '@elab/core/store/user';

import actions from './actions';
import selectors from './selectors';
import types from './types';

export function* changeEmail(dto, preventRedirects) {
    yield call(dynaFormsApi.doSubmit, '/users/current/login', dto, 'put');
    yield put(
        authActions.doLogin({
            username: dto.newEmail,
            password: dto.password,
            mergeCarts: false,
            preventRedirects,
        }),
    );
    yield take(authTypes.LOGIN_DONE);
}

export function* redirectIfAble() {
    const shouldRedirect = yield select(configSelectors.redirectFromChangeEmail);
    const successRedirectUrl = yield select(selectors.getSuccessRedirectLink);
    if (shouldRedirect && successRedirectUrl) {
        yield put(globalActions.redirect(successRedirectUrl));
    }
}

export const makeSetCurrentEmail = ({ actions }) =>
    function*() {
        const autofillEmail = yield select(configSelectors.autofillEmail);
        if (!autofillEmail) {
            return;
        }

        const uid = yield select(userSelectors.getDisplayUid);
        yield put(actions.setDataFieldValue('currentId', uid, true));
    };

export function* preventSubmitOnLoading(action) {
    yield put(uiActions.preventClick(!!action.payload));
}

export function* closeConfirmModal() {
    yield put(uiActions.closeDialog());
}

export function* doSubmit(dto) {
    const preventRedirects = yield select(configSelectors.preventRedirectsFromChangeEmail);
    try {
        yield call(changeEmail, dto, preventRedirects);
        const resetFormFields = yield select(configSelectors.resetEmailFormFields);
        if (resetFormFields) {
            yield put(actions.resetFormData());
        }
        yield put(globalActions.addMessage(emailChangeFormSuccessMessage));
        yield put(actions.setGlobalSuccess());
        yield call(redirectIfAble);
    } catch (err) {
        yield put(globalActions.addMessage(emailChangeFormErrorMessage));
    }
}

export default storeName => formId => {
    const reduxArtifacts = {
        types: getTypes(storeName),
        selectors: getSelectors(storeName),
        actions: getActions(storeName),
        storeName,
    };

    return [
        ...dynaForms.createSagaFor(
            reduxArtifacts,
            dynaFormsApi.getFormMetadata(formId || storeName),
            doSubmit,
            null,
            true,
        ),
        takeEvery(types.SET_INITIAL_DATA, makeSetCurrentEmail(reduxArtifacts)),
        takeEvery(types.SET_LOADING, preventSubmitOnLoading),
        takeEvery(types.SUBMIT, closeConfirmModal),
    ];
};
